import { clearAdminCsrfToken, readAdminCsrfToken, storeAdminCsrfToken } from './session';
import type {
  AdminAuditLogEntry,
  AdminContactSubmission,
  AdminFile,
  AdminFileInput,
  AdminFileUploadInput,
  AdminGalleryImage,
  AdminGalleryImageInput,
  AdminGalleryImageUploadInput,
  AdminLoginResult,
  AdminManagedUser,
  AdminNavItem,
  AdminNavItemInput,
  AdminPage,
  AdminPageInput,
  AdminProfile,
  AdminProfileInput,
  AdminSocialLink,
  AdminSocialLinkInput,
  AdminUser,
  AdminUserInput,
  AdminUserUpdateInput,
  PublicPage,
  PublicSite
} from './types';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    headers: { accept: 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

interface JsonRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  csrf?: boolean;
  query?: Record<string, string | number | undefined>;
}

async function requestJson<T>(path: string, options: JsonRequestOptions = {}): Promise<T> {
  const url = withQuery(path, options.query);
  const headers: Record<string, string> = { accept: 'application/json' };

  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }

  if (options.csrf) {
    const token = readAdminCsrfToken();
    if (token) {
      headers['x-csrf-token'] = token;
    }
  }

  const response = await fetch(url, {
    ...(options.method ? { method: options.method } : {}),
    credentials: 'same-origin',
    headers,
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {})
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response.json() as Promise<T>;
}

async function requestFormData<T>(path: string, body: FormData): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  const token = readAdminCsrfToken();
  if (token) {
    headers['x-csrf-token'] = token;
  }

  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body
  });

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response.json() as Promise<T>;
}

function withQuery(path: string, query?: Record<string, string | number | undefined>): string {
  if (!query) {
    return path;
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }

  const serialized = params.toString();
  return serialized ? `${path}?${serialized}` : path;
}

async function toApiError(response: Response): Promise<ApiError> {
  try {
    const body = await response.json() as { error?: string; code?: string };
    return new ApiError(body.error ?? `Request failed: ${response.status}`, response.status, body.code);
  } catch {
    return new ApiError(`Request failed: ${response.status}`, response.status);
  }
}

export function getPublicSite(): Promise<PublicSite> {
  return getJson<PublicSite>('/api/public/site');
}

export function getPublicPage(slug: string): Promise<PublicPage> {
  return getJson<PublicPage>(`/api/public/pages/${encodeURIComponent(slug)}`);
}

export async function loginAdmin(email: string, password: string): Promise<AdminLoginResult> {
  const result = await requestJson<AdminLoginResult & { csrfToken: string }>('/api/auth/login', {
    method: 'POST',
    body: { email, password }
  });

  storeAdminCsrfToken(result.csrfToken);
  return {
    user: result.user,
    requiresTotp: result.requiresTotp
  };
}

export async function logoutAdmin(): Promise<void> {
  await requestJson<{ ok: true }>('/api/auth/logout', { method: 'POST' });
  clearAdminCsrfToken();
}

export async function getCurrentAdminUser(): Promise<AdminUser> {
  const result = await requestJson<{ user: AdminUser }>('/api/admin/me');
  return result.user;
}

export async function getAdminPages(): Promise<AdminPage[]> {
  const result = await requestJson<{ pages: AdminPage[] }>('/api/admin/pages');
  return result.pages;
}

export async function createAdminPage(input: AdminPageInput): Promise<AdminPage> {
  const result = await requestJson<{ page: AdminPage }>('/api/admin/pages', {
    method: 'POST',
    body: input,
    csrf: true
  });
  return result.page;
}

export async function updateAdminPage(id: string, input: AdminPageInput): Promise<AdminPage> {
  const result = await requestJson<{ page: AdminPage }>(`/api/admin/pages/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    csrf: true
  });
  return result.page;
}

export async function deleteAdminPage(id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/admin/pages/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    csrf: true
  });
}

export async function getAdminNavItems(): Promise<AdminNavItem[]> {
  const result = await requestJson<{ items: AdminNavItem[] }>('/api/admin/nav');
  return result.items;
}

export async function saveAdminNavItems(items: AdminNavItemInput[]): Promise<void> {
  await requestJson<{ ok: true }>('/api/admin/nav', {
    method: 'PUT',
    body: { items },
    csrf: true
  });
}

export async function getAdminProfiles(): Promise<AdminProfile[]> {
  const result = await requestJson<{ profiles: AdminProfile[] }>('/api/admin/profiles');
  return result.profiles;
}

export async function createAdminProfile(input: AdminProfileInput): Promise<AdminProfile> {
  const result = await requestJson<{ profile: AdminProfile }>('/api/admin/profiles', {
    method: 'POST',
    body: input,
    csrf: true
  });
  return result.profile;
}

export async function updateAdminProfile(id: string, input: AdminProfileInput): Promise<AdminProfile> {
  const result = await requestJson<{ profile: AdminProfile }>(`/api/admin/profiles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    csrf: true
  });
  return result.profile;
}

export async function deleteAdminProfile(id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/admin/profiles/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    csrf: true
  });
}

export async function getAdminSocialLinks(): Promise<AdminSocialLink[]> {
  const result = await requestJson<{ links: AdminSocialLink[] }>('/api/admin/social-links');
  return result.links;
}

export async function saveAdminSocialLinks(links: AdminSocialLinkInput[]): Promise<void> {
  await requestJson<{ ok: true }>('/api/admin/social-links', {
    method: 'PUT',
    body: { links },
    csrf: true
  });
}

export async function getAdminUsers(): Promise<AdminManagedUser[]> {
  const result = await requestJson<{ users: AdminManagedUser[] }>('/api/admin/users');
  return result.users;
}

export async function createAdminUser(input: AdminUserInput): Promise<AdminManagedUser> {
  const result = await requestJson<{ user: AdminManagedUser }>('/api/admin/users', {
    method: 'POST',
    body: input,
    csrf: true
  });
  return result.user;
}

export async function updateAdminUser(id: string, input: AdminUserUpdateInput): Promise<AdminManagedUser> {
  const result = await requestJson<{ user: AdminManagedUser }>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    csrf: true
  });
  return result.user;
}

export async function deleteAdminUser(id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    csrf: true
  });
}

export async function getAdminContactSubmissions(): Promise<AdminContactSubmission[]> {
  const result = await requestJson<{ submissions: AdminContactSubmission[] }>('/api/admin/contact-submissions');
  return result.submissions;
}

export async function updateAdminContactHandled(id: string, handled: boolean): Promise<AdminContactSubmission> {
  const result = await requestJson<{ submission: AdminContactSubmission }>(
    `/api/admin/contact-submissions/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: { handled },
      csrf: true
    }
  );
  return result.submission;
}

export async function getAdminAuditLog(filters: {
  action?: string;
  entityType?: string;
  actorUserId?: string;
  limit?: number;
} = {}): Promise<AdminAuditLogEntry[]> {
  const result = await requestJson<{ entries: AdminAuditLogEntry[] }>('/api/admin/audit-log', {
    query: filters
  });
  return result.entries;
}

export async function getAdminGalleryImages(): Promise<AdminGalleryImage[]> {
  const result = await requestJson<{ images: AdminGalleryImage[] }>('/api/admin/gallery-images');
  return result.images;
}

export async function createAdminGalleryImage(input: AdminGalleryImageInput): Promise<AdminGalleryImage> {
  const result = await requestJson<{ image: AdminGalleryImage }>('/api/admin/gallery-images', {
    method: 'POST',
    body: input,
    csrf: true
  });
  return result.image;
}

export async function uploadAdminGalleryImage(input: AdminGalleryImageUploadInput): Promise<AdminGalleryImage> {
  const body = new FormData();
  body.append('file', input.file, input.file.name);
  body.set('caption', input.caption);
  body.set('altText', input.altText);
  body.set('status', input.status);
  body.set('sortOrder', String(input.sortOrder));

  const result = await requestFormData<{ image: AdminGalleryImage }>('/api/admin/gallery-images/upload', body);
  return result.image;
}

export async function updateAdminGalleryImage(id: string, input: AdminGalleryImageInput): Promise<AdminGalleryImage> {
  const result = await requestJson<{ image: AdminGalleryImage }>(`/api/admin/gallery-images/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    csrf: true
  });
  return result.image;
}

export async function deleteAdminGalleryImage(id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/admin/gallery-images/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    csrf: true
  });
}

export async function getAdminFiles(): Promise<AdminFile[]> {
  const result = await requestJson<{ files: AdminFile[] }>('/api/admin/files');
  return result.files;
}

export async function createAdminFile(input: AdminFileInput): Promise<AdminFile> {
  const result = await requestJson<{ file: AdminFile }>('/api/admin/files', {
    method: 'POST',
    body: input,
    csrf: true
  });
  return result.file;
}

export async function uploadAdminFile(input: AdminFileUploadInput): Promise<AdminFile> {
  const body = new FormData();
  body.append('file', input.file, input.file.name);
  body.set('label', input.label);
  body.set('description', input.description);
  body.set('status', input.status);
  body.set('sortOrder', String(input.sortOrder));

  const result = await requestFormData<{ file: AdminFile }>('/api/admin/files/upload', body);
  return result.file;
}

export async function updateAdminFile(id: string, input: AdminFileInput): Promise<AdminFile> {
  const result = await requestJson<{ file: AdminFile }>(`/api/admin/files/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
    csrf: true
  });
  return result.file;
}

export async function deleteAdminFile(id: string): Promise<void> {
  await requestJson<{ ok: true }>(`/api/admin/files/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    csrf: true
  });
}
